import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import icons from "../../../assets/images/constantIcons";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";

const AddRolesForm = ({ isOpen, onClose, fetchRoles }) => {
  const [modules, setModules] = useState([]);
  const [existingRoles, setExistingRoles] = useState([]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await apiService.get("roles/module_names");
        setModules(response.modules);
      } catch (errors) {
        handleError(errors);
      }
    };

    const fetchExistingRoles = async () => {
      try {
        const response = await apiService.get("roles");
        setExistingRoles(response.roles.map((role) => role.name.toLowerCase()));
      } catch (error) {
        console.error("Failed to fetch existing roles:", error);
      }
    };

    fetchModules();
    fetchExistingRoles();
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      module_access: [],
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(3, "Role name must be at least 3 characters long")
        .max(30, "Role name cannot exceed 30 characters")
        .matches(
          /^[A-Za-z\s]+$/,
          "Role name cannot contain numbers or special characters"
        )
        .required("Role name is required")
        .test(
          "unique-name",
          "Role name already exit",
          (value) => !existingRoles.includes(value.toLowerCase())
        ),
      module_access: Yup.array().min(1, "Select at least one module"),
    }),
    onSubmit: async (values) => {
      try {
        const response = await apiService.post("roles", values);
        if (response.status === 201) {
          onClose();
          toast.success(response.message, { duration: 2000 });
          formik.resetForm();
          fetchRoles();
        }
      } catch (errors) {
        handleError(errors)
      }
    },
  });

  return (
    isOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white rounded-lg p-4 s:p-6 w-[310px] s:w-[350px] md:w-[460px] h-auto overflow-y-auto relative">
          <div
            className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
            onClick={() => {
              formik.resetForm();
              onClose();
            }}
          >
            <icons.IoClose className="text-green700" />
          </div>
          <h2 className="text-xl font-semibold mb-5 text-G500">Add Role</h2>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Role Name */}
            <div className="text-G300">
              <label className="block text-sm mb-2">Role Name</label>
              <input
                type="text"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="inputtext"
                placeholder="Role Name"
              />
              {formik.errors.name && formik.touched.name && (
                <div className="text-red text-sm">{formik.errors.name}</div>
              )}
            </div>

            {/* Multi-Select Dropdown with Checkboxes */}
            <div className="text-G300">
              <label className="block font-normal text-sm mb-2">
                Module Access
              </label>
              <Select
                options={(modules || []).map((module) => ({
                  value: module.toLowerCase(),
                  label: module.charAt(0).toUpperCase() + module.slice(1), // capital first letter
                }))}
                isMulti
                name="module_access"
                className="basic-multi-select"
                classNamePrefix="select"
                value={(formik.values.module_access || []).map((module) => ({
                  value: module.toLowerCase(), // ensure value remains lowercase
                  label: module.charAt(0).toUpperCase() + module.slice(1), // capital first letter to dispaly in ui
                }))}
                onChange={(selectedOptions) => {
                  formik.setFieldValue(
                    "module_access",
                    selectedOptions.map((option) => option.value) // store lowercase values
                  );
                }}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    boxShadow: "none",
                    borderColor: "#EDEDED",
                  }),
                }}
              />

              {formik.errors.module_access && formik.touched.module_access && (
                <div className="text-red text-sm">
                  {formik.errors.module_access}
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  formik.resetForm();
                  onClose();
                }}
                className="w-[75px] sm:w-[93px] text-sm py-2 text-center border border-green text-green rounded-lg"
              >
                Close
              </button>
              <button
                type="submit"
                className="w-[75px] sm:w-[93px] text-sm text-center py-2 bg-green text-white rounded-lg"
              >
                Add
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

export default AddRolesForm;
